<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import types from 'dashboard/store/mutation-types';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const STATUS_KEY = 'status_ia';
const IA_ON = 'ia_ligada';
const IA_OFF = 'ia_desligada';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const isLoading = ref(false);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);

const currentChat = computed(() => getters.getSelectedChat.value);

const currentContact = computed(() => {
  const senderId = currentChat.value?.meta?.sender?.id;
  if (!senderId) return null;
  return store.getters['contacts/getContact'](senderId);
});

const statusIa = computed(
  () => currentContact.value?.custom_attributes?.[STATUS_KEY] || IA_ON
);

const isIaOn = computed(() => statusIa.value !== IA_OFF);

const setIaStatus = async next => {
  const chat = currentChat.value;
  const contact = currentContact.value;
  if (!chat?.id || !contact?.id || isLoading.value || statusIa.value === next)
    return;

  closeDropdown();
  isLoading.value = true;
  try {
    const { data } = await ConversationAPI.toggleStatusIa({
      conversationId: chat.id,
      statusIa: next,
    });
    if (data?.payload) {
      store.commit(`contacts/${types.EDIT_CONTACT}`, data.payload);
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.response?.message ||
        t('CONVERSATION.HEADER.IA_TOGGLE_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="flex relative justify-end items-center ia-toggle-actions">
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="isIaOn ? 'outline-n-teal-8' : 'outline-n-container'"
    >
      <Button
        :label="
          isIaOn
            ? t('CONVERSATION.HEADER.IA_LIGADA')
            : t('CONVERSATION.HEADER.IA_DESLIGADA')
        "
        size="sm"
        variant="faded"
        :color="isIaOn ? 'teal' : 'slate'"
        no-animation
        :is-loading="isLoading"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        @click="toggleDropdown()"
      />
      <Button
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        variant="faded"
        :color="isIaOn ? 'teal' : 'slate'"
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        trailing-icon
        @click="toggleDropdown()"
      />
    </ButtonGroup>
    <span
      class="hidden xl:inline-block ltr:ml-1.5 rtl:mr-1.5 text-xxs leading-none text-n-slate-11 whitespace-nowrap"
    >
      {{ t('CONVERSATION.HEADER.IA_SCOPE_HINT') }}
    </span>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.HEADER.ENABLE_IA')"
            :variant="isIaOn ? 'ghost' : 'faded'"
            :color="isIaOn ? 'slate' : 'teal'"
            sm
            start
            :disabled="isIaOn"
            icon="i-lucide-bot"
            class="w-full"
            @click="() => setIaStatus(IA_ON)"
          />
        </WootDropdownItem>
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.HEADER.PAUSE_IA')"
            :variant="!isIaOn ? 'ghost' : 'faded'"
            :color="!isIaOn ? 'slate' : 'ruby'"
            sm
            start
            :disabled="!isIaOn"
            icon="i-lucide-bot-off"
            class="w-full"
            @click="() => setIaStatus(IA_OFF)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
  </div>
</template>
